import React from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader } from '../ui/Card'
import { Button } from '../ui/Button'
import { Badge } from '../ui/Badge'
import { 
  Star, 
  Clock, 
  MapPin, 
  Phone, 
  ExternalLink,
  Users,
  Leaf,
  Heart,
  Plane
} from 'lucide-react'
import { Vendor } from '../../services/mealmateService'
import AIVisual from '../ui/AIVisual'

interface VendorCardProps {
  vendor: Vendor
  onSelect: () => void
  onQuickView: () => void
  onCoordinate?: () => void
}

const VendorCard: React.FC<VendorCardProps> = ({ vendor, onSelect, onQuickView, onCoordinate }) => {
  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating) 
            ? 'text-yellow-400 fill-current' 
            : i < rating 
            ? 'text-yellow-400 fill-current opacity-50'
            : 'text-theme-text-muted'
        }`}
      />
    ))
  }

  const getCuisineBadgeColor = (cuisine: string) => {
    const colors: Record<string, string> = {
      'Italian': 'bg-red-100 text-red-800',
      'Mexican': 'bg-yellow-100 text-yellow-800',
      'Asian': 'bg-orange-100 text-orange-800',
      'Mediterranean': 'bg-blue-100 text-blue-800',
      'American': 'bg-blue-100 text-blue-800',
      'Healthy': 'bg-green-100 text-green-800',
      'Organic': 'bg-emerald-100 text-emerald-800',
      'Family': 'bg-purple-100 text-purple-800'
    }
    return colors[cuisine] || 'bg-theme-surface-light text-theme-text-primary'
  }

  const getDietaryBadgeColor = (dietary: string) => {
    const colors: Record<string, string> = {
      'Vegetarian': 'bg-green-100 text-green-800',
      'Vegan': 'bg-emerald-100 text-emerald-800',
      'Gluten-Free': 'bg-orange-100 text-orange-800',
      'Keto': 'bg-purple-100 text-purple-800',
      'Paleo': 'bg-amber-100 text-amber-800',
      'Organic': 'bg-lime-100 text-lime-800'
    }
    return colors[dietary] || 'bg-theme-surface-light text-theme-text-primary'
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="h-full hover:shadow-lg transition-all duration-300 border-2 hover:border-green-200 group">
        <div className="relative">
          {/* Vendor Image */}
          <div className="h-48 bg-gradient-to-br from-green-100 to-emerald-100 rounded-t-xl overflow-hidden">
            <AIVisual
              src={vendor.logo || `https://trae-api-us.mchost.guru/api/ide/v1/text_to_image?prompt=Professional%20restaurant%20logo%20design%2C%20${vendor.name.replace(' ', '%20')}%2C%20modern%20clean%20branding%2C%20food%20service%20identity%2C%20vector%20style%2C%20professional%20corporate%20design&image_size=square`}
              alt={vendor.name}
              fallbackType="health"
              className="w-full h-full"
            />
            
            {/* Rating Badge */}
            <div className="absolute top-4 right-4 bg-theme-surface/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
              <Star className="w-4 h-4 text-yellow-400 fill-current" />
              <span className="text-sm font-semibold text-theme-text-primary">{vendor.rating}</span>
            </div>

            {/* Availability Status */}
            {vendor.isActive && (
              <div className="absolute top-4 left-4 bg-green-500 text-white rounded-full px-3 py-1 flex items-center space-x-1">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-xs font-medium">Open</span>
              </div>
            )}
          </div>

          {/* Quick Action Overlay */}
          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="space-y-3">
              <Button
                onClick={onQuickView}
                className="bg-theme-text-primary text-theme-text-inverse hover:bg-theme-text-primary/90 w-full"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                View Menu
              </Button>
              <Button
                variant="outline"
                onClick={onSelect}
                className="border-theme-text-primary text-theme-text-primary hover:bg-theme-text-primary/10 w-full"
              >
                <Phone className="w-4 h-4 mr-2" />
                Contact Vendor
              </Button>
            </div>
          </div>
        </div>

        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="text-xl font-bold text-theme-text-primary mb-1">{vendor.name}</h3>
              <p className="text-theme-text-secondary text-sm line-clamp-2">{vendor.description}</p>
            </div>
          </div>

          {/* Rating and Reviews */}
          <div className="flex items-center space-x-2 mt-3">
            <div className="flex items-center space-x-1">
              {renderStars(vendor.rating)}
            </div>
            <span className="text-sm text-theme-text-secondary">({vendor.rating})</span>
          </div>

          {/* Delivery Info */}
          <div className="flex items-center space-x-4 mt-3 text-sm text-theme-text-secondary">
            <div className="flex items-center space-x-1">
              <Clock className="w-4 h-4" />
              <span>{vendor.deliveryTime}</span>
            </div>
            <div className="flex items-center space-x-1">
              <MapPin className="w-4 h-4" />
              <span>{vendor.deliveryFee === 0 ? 'Free Delivery' : `$${vendor.deliveryFee} delivery`}</span>
            </div>
            {vendor.minimumOrder > 0 && (
              <div className="flex items-center space-x-1">
                <Users className="w-4 h-4" />
                <span>Min $${vendor.minimumOrder}</span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          {/* Cuisine Types */}
          <div className="flex flex-wrap gap-2 mb-4">
            {vendor.cuisineTypes.slice(0, 3).map((cuisine) => (
              <Badge
                key={cuisine}
                className={`${getCuisineBadgeColor(cuisine)} text-xs px-2 py-1`}
              >
                {cuisine}
              </Badge>
            ))}
            {vendor.cuisineTypes.length > 3 && (
              <Badge className="bg-theme-surface text-theme-text-secondary text-xs px-2 py-1">
                +{vendor.cuisineTypes.length - 3}
              </Badge>
            )}
          </div>

          {/* Dietary Options */}
          {vendor.dietaryOptions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {vendor.dietaryOptions.slice(0, 4).map((dietary) => (
                <Badge
                  key={dietary}
                  className={`${getDietaryBadgeColor(dietary)} text-xs px-2 py-1 flex items-center space-x-1`}
                >
                  <Leaf className="w-3 h-3" />
                  <span>{dietary}</span>
                </Badge>
              ))}
            </div>
          )}

          {/* Contact Information */}
          <div className="space-y-2 text-sm text-theme-text-secondary border-t border-theme-border pt-4">
            <div className="flex items-center space-x-2">
              <Phone className="w-4 h-4" />
              <span>{vendor.contactInfo.phone}</span>
            </div>
            {vendor.contactInfo.website && (
              <div className="flex items-center space-x-2">
                <ExternalLink className="w-4 h-4" />
                <a 
                  href={vendor.contactInfo.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700"
                >
                  Visit Website
                </a>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 mt-6">
            <div className="flex space-x-3">
              <Button
                onClick={onSelect}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                View Details
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  // Add to favorites functionality
                  console.log('Add to favorites:', vendor.name)
                }}
                className="border-theme-border text-theme-text-secondary hover:bg-theme-surface"
              >
                <Heart className="w-4 h-4" />
              </Button>
            </div>
            {onCoordinate && (
              <Button
                onClick={onCoordinate}
                className="w-full bg-theme-text-primary hover:bg-theme-text-primary/90 text-theme-text-inverse flex items-center justify-center space-x-2"
              >
                <Plane className="w-4 h-4" />
                <span>Coordinate Precision Meal</span>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export default VendorCard